/* eslint-disable @typescript-eslint/no-redundant-type-constituents */
import format from "string-format";
import { JsonElementType, JsonObject, JsonProperty } from "ta-json";

import type { MapStringTo, Nullable, NullableResultPromise, Optional } from "../../base-types";
import type { IContentHubClient } from "../../clients/content-hub-client";
import CultureInfo from "../../culture-info";
import ErrorMessages from "../../error-messages";
import { ArgumentError } from "../../errors/argument-error";
import { NotLoadedError } from "../../errors/not-loaded-error";
import { SchemaError } from "../../errors/schema-error";
import Guard from "../../guard";
import Link from "../../link";
import { IDirtyTracking } from "../dirty-tracking/dirty-tracking";
import { CultureLoadOption } from "../querying/culture-load-option";
import { EntityLoadConfiguration } from "../querying/entity-load-configuration";
import { MemberLoadOption } from "../querying/member-load-option";
import { IPropertyLoadOption } from "../querying/property-load-option";
import { IRelationLoadOption } from "../querying/relation-load-option";
import { CultureInsensitiveProperty, ICultureInsensitiveProperty } from "./culture-insensitive-property";
import { CultureSensitiveProperty, ICultureSensitiveProperty } from "./culture-sensitive-property";
import { PropertyDataType, PropertyValue } from "./data-type";
import { IEntity } from "./entity";
import { EntityConstructionArgs } from "./entity-construction-args";
import { IEntityDefinition } from "./entity-definition";
import { IMember } from "./member";
import { MemberDefinitionType } from "./member-definition-type";
import { IProperty, PropertyBase } from "./property";
import { PropertyManager } from "./property-manager";
import { IRelatedPath } from "./related-path";
import { IRelation, RelationBase, RelationType } from "./relation";
import { RelationManager } from "./relation-manager";
import { RelationRole } from "./relation-role";
import { ChildToManyParentsRelation } from "./relations/child-to-many-parents-relation";
import { ChildToOneParentRelation } from "./relations/child-to-one-parent-relation";
import { ParentToManyChildrenRelation } from "./relations/parent-to-many-children-relation";
import { ParentToOneChildRelation } from "./relations/parent-to-one-child-relation";
import { IRendition } from "./rendition";

@JsonObject()
export abstract class EntityBase implements IEntity {
    protected readonly _client: IContentHubClient;
    protected readonly _members: MapStringTo<IMember> = {};

    protected readonly _propertyManager: PropertyManager;
    protected readonly _relationManager: RelationManager;

    @JsonProperty("id")
    id?: number;

    @JsonProperty("identifier")
    identifier: Nullable<string>;

    @JsonProperty("hasPublicLink")
    hasPublicLink?: boolean;

    @JsonProperty("annotationCount")
    annotationCount?: number;

    @JsonProperty("masterFileModifiedOn")
    masterFileModifiedOn?: Date;

    @JsonProperty("gatewayLinks")
    gatewayLinks?: Record<string, Link>;

    @JsonProperty("publicLink")
    publicLink?: string;

    @JsonProperty("publicCollectionLink")
    publicCollectionLink?: string;

    definitionName: string;

    @JsonProperty("locked_by")
    readonly lockedBy: Nullable<number>;

    @JsonProperty("locked_on")
    readonly lockedOn: Nullable<Date>;

    @JsonProperty("is_root_taxonomy_item")
    isRootTaxonomyItem: boolean;

    @JsonProperty("is_path_root")
    isPathRoot: boolean;

    @JsonProperty("inherits_security")
    inheritsSecurity: boolean;

    @JsonProperty("is_system_owned")
    isSystemOwned: boolean;

    @JsonProperty("version")
    version: number;

    @JsonProperty("cultures")
    @JsonElementType(String)
    cultures: Array<CultureInfo>;

    @JsonProperty("is_current_user_default")
    isCurrentUserDefault?: boolean;

    @JsonProperty("is_enabled")
    isEnabled?: boolean;

    @JsonProperty("modules")
    @JsonElementType(String)
    modules?: Array<string>;

    @JsonProperty("combinedPublishStatus")
    combinedPublishStatus?: string;

    @JsonProperty("combinedPublishStatusDetails")
    combinedPublishStatusDetails?: string;

    @JsonProperty("properties")
    @JsonElementType(PropertyBase)
    protected _properties: Array<IProperty>;

    get properties(): ReadonlyArray<IProperty> {
        return this._propertyManager.getProperties();
    }

    @JsonProperty("relations")
    @JsonElementType(RelationBase)
    protected _relations: Array<IRelation>;

    get relations(): ReadonlyArray<IRelation> {
        return this._relationManager.getRelations();
    }

    @JsonProperty("created_on")
    readonly createdOn?: Date;

    @JsonProperty("created_by")
    readonly createdBy?: number;

    @JsonProperty("modified_on")
    readonly modifiedOn?: Date;

    @JsonProperty("modified_by")
    readonly modifiedBy?: number;

    @JsonProperty("external_data_identifier")
    readonly externalDataIdentifier?: string;

    isTracking: boolean;

    get canDoLazyLoading(): boolean {
        return !this.isNew && !!this.id;
    }

    get isDirty(): boolean {
        if (!this.isTracking) {
            return false;
        }

        for (const dirtyTracker of this.getDirtyTrackingMembers()) {
            if (dirtyTracker.isDirty) {
                return true;
            }
        }
        return false;
    }

    get isNew(): boolean {
        return !this.id || this.id <= 0;
    }

    abstract get renditions(): Array<IRendition>;

    abstract get relatedPaths(): Array<IRelatedPath>;

    constructor(client: IContentHubClient, args: EntityConstructionArgs) {
        Guard.notNullOrUndefined(client);
        Guard.notNullOrUndefined(args);
        Guard.arrayNoneNullOrInvariantCulture(args.cultures);
        Guard.arrayNoneNullOrUndefined(args.properties);
        Guard.arrayNoneNullOrUndefined(args.relations);

        this._client = client;
        this.definitionName = args.definitionName;
        this.id = args.id || undefined;
        this.identifier = args.identifier;
        this.cultures = args.cultures;

        this._propertyManager = new PropertyManager(client, args.properties, this);
        this._relationManager = new RelationManager(client, args.relations, this);
    }

    private isProperty(member: IMember): member is IProperty {
        return member && member.definitionType === MemberDefinitionType.Property;
    }

    getProperty<T extends IProperty = IProperty>(name: string): Nullable<T> {
        Guard.stringNotNullOrEmpty(name);

        const property = this._propertyManager.getProperty<T>(name);

        if (property == null) {
            const errorMessage = format(
                ErrorMessages.Entity.PropertyNotFound,
                name,
                this.id!.toString(),
                this.definitionName
            );
            throw new NotLoadedError(errorMessage);
        }

        return property;
    }

    async getPropertyAsync<T extends IProperty = IProperty>(
        name: string,
        loadOption: MemberLoadOption = MemberLoadOption.LazyLoading,
        culture: Optional<CultureInfo>
    ): NullableResultPromise<T> {
        Guard.stringNotNullOrEmpty(name);
        Guard.notNullOrUndefined(loadOption);

        let property = this._propertyManager.getProperty<T>(name);

        if (property == null && loadOption == MemberLoadOption.LazyLoading) {
            property = await this._propertyManager.loadPropertyAsync<T>(name, culture);

            if (property == null) {
                const errorMessage = format(ErrorMessages.Entity.PropertyDoesNotExist, name, this.definitionName);
                throw new SchemaError(errorMessage);
            }
        }

        if (property == null) {
            const errorMessage = format(
                ErrorMessages.Entity.PropertyNotFound,
                name,
                this.id!.toString(),
                this.definitionName
            );
            throw new SchemaError(errorMessage);
        }

        return property;
    }

    getPropertyValue<T extends PropertyDataType | unknown = unknown, B extends boolean = false>(
        name: string,
        culture?: CultureInfo
    ): PropertyValue<T, B> {
        Guard.stringNotNullOrEmpty(name);

        const property = this.getProperty(name);

        if (property == null) {
            const errorMessage = format(
                ErrorMessages.Entity.PropertyNotFound,
                name,
                this.id!.toString(),
                this.definitionName
            );
            throw new NotLoadedError(errorMessage);
        } else if (property.isMultiLanguage && culture == null) {
            throw new ArgumentError(ErrorMessages.Entity.CultureRequired);
        } else if (culture != null && !property.isMultiLanguage) {
            throw new ArgumentError(ErrorMessages.Entity.CultureNotSupported);
        }

        if (property.isMultiLanguage) {
            const cultureSensitiveProp = property as CultureSensitiveProperty<T, B>;

            return cultureSensitiveProp.getValue(culture!);
        } else {
            const cultureInsensitiveProp = property as CultureInsensitiveProperty<T, B>;

            return cultureInsensitiveProp.getValue();
        }
    }

    async getPropertyValueAsync<T extends PropertyDataType | unknown = unknown, B extends boolean = false>(
        name: string,
        loadOption: MemberLoadOption = MemberLoadOption.LazyLoading,
        culture?: CultureInfo
    ): NullableResultPromise<PropertyValue<T, B>> {
        Guard.stringNotNullOrEmpty(name);
        Guard.notNullOrUndefined(loadOption);

        const property = await this.getPropertyAsync(name, loadOption, culture);

        if (property == null) {
            const errorMessage = format(ErrorMessages.Entity.PropertyDoesNotExist, name, this.definitionName);
            throw new SchemaError(errorMessage);
        } else if (property.isMultiLanguage && culture == null) {
            throw new ArgumentError(ErrorMessages.Entity.CultureRequired);
        } else if (culture != null && !property.isMultiLanguage) {
            throw new ArgumentError(ErrorMessages.Entity.CultureNotSupported);
        }

        if (property.isMultiLanguage) {
            const cultureSensitiveProp = property as CultureSensitiveProperty<T, B>;

            return cultureSensitiveProp.getValue(culture!);
        } else {
            const cultureInsensitiveProp = property as CultureInsensitiveProperty<T, B>;

            return cultureInsensitiveProp.getValue();
        }
    }

    setPropertyValue<T extends PropertyDataType, B extends boolean = false>(
        name: string,
        value: PropertyValue<T, B>,
        culture?: CultureInfo
    ): void {
        Guard.stringNotNullOrEmpty(name);

        const property = this._propertyManager.getProperty(name);

        if (property == null) {
            const errorMessage = format(
                ErrorMessages.Entity.PropertyNotFound,
                name,
                this.id!.toString(),
                this.definitionName
            );
            throw new NotLoadedError(errorMessage);
        } else if (property.isMultiLanguage && culture == null) {
            throw new ArgumentError(ErrorMessages.Entity.CultureRequired);
        } else if (culture != null && !property.isMultiLanguage) {
            throw new ArgumentError(ErrorMessages.Entity.CultureRequired);
        }

        if (property.isMultiLanguage) {
            (property as ICultureSensitiveProperty<T, B>).setValue(culture!, value);
        } else {
            (property as ICultureInsensitiveProperty<T, B>).setValue(value);
        }
    }

    getRelation(name: string, role?: RelationRole, _returnNull: boolean = false): Nullable<RelationType> {
        Guard.stringNotNullOrEmpty(name);

        const relation = this._relationManager.getRelation(name, role);

        if (relation == null) {
            if (_returnNull) {
                return null;
            }

            const errorMessage = format(
                ErrorMessages.Entity.RelationNotFound,
                name,
                this.id!.toString(),
                this.definitionName
            );
            throw new NotLoadedError(errorMessage);
        } else if (RelationBase.isChildToManyParentsRelation(relation)) {
            return relation as ChildToManyParentsRelation;
        } else if (RelationBase.isChildToOneParentRelation(relation)) {
            return relation as ChildToOneParentRelation;
        } else if (RelationBase.isParentToManyChildrenRelation(relation)) {
            return relation as ParentToManyChildrenRelation;
        } else if (RelationBase.isParentToOneChildRelation(relation)) {
            return relation as ParentToOneChildRelation;
        }

        return null;
    }

    async getRelationAsync(
        name: string,
        role?: RelationRole,
        loadOption?: MemberLoadOption
    ): NullableResultPromise<RelationType> {
        Guard.stringNotNullOrEmpty(name);

        const relation = this.getRelation(name, role, true);

        if (relation) {
            if (
                (RelationBase.isChildToManyParentsRelation(relation) ||
                    RelationBase.isParentToManyChildrenRelation(relation)) &&
                !relation.isFullyLoaded
            ) {
                return (await this._relationManager.loadRelationAsync(name, role, true)) as RelationType;
            }
        } else {
            if (loadOption == MemberLoadOption.LazyLoading) {
                // Scoped to be able to redefine relation as type RelationType does not match IRelation. //TODO generics?
                const relation = await this._relationManager.loadRelationAsync(name, role);

                if (relation == null) {
                    const errorMessage = format(ErrorMessages.Entity.RelationDoesNotExist, name, this.definitionName);
                    throw new SchemaError(errorMessage);
                }

                if (RelationBase.isChildToManyParentsRelation(relation)) {
                    return relation as ChildToManyParentsRelation;
                } else if (RelationBase.isChildToOneParentRelation(relation)) {
                    return relation as ChildToOneParentRelation;
                } else if (RelationBase.isParentToManyChildrenRelation(relation)) {
                    return relation as ParentToManyChildrenRelation;
                } else if (RelationBase.isParentToOneChildRelation(relation)) {
                    return relation as ParentToOneChildRelation;
                }
            }
        }

        return relation;
    }

    async loadPropertiesAsync(
        propertyLoadOption: IPropertyLoadOption,
        culture: Optional<CultureInfo>
    ): Promise<boolean> {
        if (!this.canDoLazyLoading || propertyLoadOption == null) {
            return false;
        }

        return this._propertyManager.loadPropertiesAsync(propertyLoadOption, culture);
    }

    async loadRelationsAsync(relationLoadOption: IRelationLoadOption): Promise<boolean> {
        if (!this.canDoLazyLoading || relationLoadOption == null) {
            return false;
        }

        return this._relationManager.loadRelationsAsync(relationLoadOption);
    }

    async loadMembersAsync(
        propertyLoadOption: IPropertyLoadOption,
        relationLoadOption: IRelationLoadOption
    ): Promise<boolean> {
        if (!this.canDoLazyLoading) {
            return false;
        }

        const hasMissingProperties = this._propertyManager.hasMissingProperties(propertyLoadOption);
        const hasMissingRelations = this._relationManager.hasMissingRelations(relationLoadOption);

        if (!hasMissingProperties && !hasMissingRelations) {
            return false;
        }

        const cultureLoadOption = new CultureLoadOption([...this.cultures]);
        const loadConfiguration = new EntityLoadConfiguration(
            cultureLoadOption,
            propertyLoadOption,
            relationLoadOption
        );

        const tempEntity = await this._client.entities.getAsync(this.id!, loadConfiguration);

        // Import missing members
        this._propertyManager.importMissingProperties(tempEntity);
        this._relationManager.importMissingRelations(tempEntity);

        return true;
    }

    async getEntityDefinitionAsync(): Promise<IEntityDefinition> {
        return (await this._client.entityDefinitions.getCachedAsync(this.definitionName))!;
    }

    abstract getRendition(name: string): Nullable<IRendition>;

    abstract getRelatedPath(name: string): Nullable<IRelatedPath>;

    abstract getPermissionsAsync(): Promise<Array<string>>;

    startTracking(): void {
        if (this.isTracking) return;

        this.isTracking = true;
        for (const member of this.getDirtyTrackingMembers()) {
            member.startTracking();
        }
    }

    markClean(): void {
        for (const member of this.getDirtyTrackingMembers()) {
            member.markClean();
        }
    }

    private getDirtyTrackingMembers(): Array<IDirtyTracking> {
        const properties = this._propertyManager.getProperties();
        const relations = this._relationManager.getRelations();

        const list: Array<IDirtyTracking> = [...properties, ...relations];

        return list;
    }
}
